import { createStrictContext } from 'utils/createContext'
import {
  useEditRequestForInformationMutation,
  useGetRequestsForInformationQuery,
  useGetRfiTrackerTableMetadataQuery,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { DraftSubmitRFIModalData } from '../components/RFITrackerTable/components/DraftSubmitRFIModal'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DisciplineResponseModalData } from '../components/RFITrackerTable/components/DisciplineResponseModal'
import { EditRFIResponseModalData } from '../components/RFITrackerTable/hooks/useExpandOptions/components/EditResponseModal'
import { RequestForInformation, RFITrackerContext } from './types'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<RFITrackerContext>('RFITrackerContext')

export const useRFITrackerContext = useContextFn

export const RFITrackerProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const {
    refetch,
    loading: fetching,
    data,
  } = useGetRequestsForInformationQuery({
    ...DefaultQueryHookOptions,
    variables: {
      projectId,
      requestForInformationListInput: {
        projectId: {
          equals: projectId,
        },
      },
    },
  })
  const {
    loading: metaLoading,
    refetch: metaRefetch,
    data: metadata,
  } = useGetRfiTrackerTableMetadataQuery({
    ...DefaultQueryHookOptions,
    variables: {
      globalId: projectId!,
    },
  })
  const { rfiOrganizations, rfiResponseDisciplines, rfiQuickLinks } =
    metadata?.project ?? {}
  const [edit] = useEditRequestForInformationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editRequestForInformation: {
        requestForInformation: { formattedNumber },
      },
    }) => simpleEditNotification(formattedNumber),
  })
  const organizationOptions = useOptions({
    items: rfiOrganizations,
    value: 'id',
    label: 'name',
    sort: 'name',
    tooltip: ({ description }) => description,
  })
  const draftSubmitModal = useCIMSModalValues<DraftSubmitRFIModalData>()
  const disciplineResponseModal =
    useCIMSModalValues<DisciplineResponseModalData>()
  const revertRFIModal = useCIMSModalValues<RequestForInformation>()
  const editResponseModal = useCIMSModalValues<EditRFIResponseModalData>()

  return (
    <Provider
      value={{
        refetch,
        fetching,
        records: data?.requestsForInformation?.records ?? [],
        metadata: {
          loading: metaLoading,
          refetch: metaRefetch,
          rfiOrganizations: rfiOrganizations ?? [],
          rfiResponseDisciplines: rfiResponseDisciplines ?? [],
          rfiQuickLinks: rfiQuickLinks ?? [],
        },
        edit,
        selectOptions: {
          organizationOptions,
        },
        draftSubmitModal,
        disciplineResponseModal,
        revertRFIModal,
        editResponseModal,
      }}
    >
      {children}
    </Provider>
  )
}
